import React, { useEffect, useRef, useState } from 'react';
import { useStaticQuery, graphql, withPrefix } from 'gatsby';
import styled from 'styled-components';
import { Icon } from '@components/icons';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledPublicationsSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  .publications-grid {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
    gap: 20px;
    width: 100%;
    margin-top: 40px;

    @media (max-width: 480px) {
      gap: 10px;
      margin-top: 30px;
    }
  }

  .publication-filters {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin-top: 0;

    @media (max-width: 480px) {
      gap: 10px;
    }
  }

  .filter-button {
    ${({ theme }) => theme.mixins.smallButton};
    padding: 0.65rem 0.9rem;
    min-height: 44px;
    border-color: var(--border-gold);
    color: rgba(240, 236, 224, 0.72);
    touch-action: manipulation;

    @media (max-width: 480px) {
      padding: 0.5rem 0.75rem;
      font-size: var(--fz-xxs);
    }

    &.active {
      border-color: var(--green);
      background: linear-gradient(180deg, rgba(201, 162, 39, 0.18), rgba(201, 162, 39, 0.08));
      color: var(--gold-light);
      box-shadow: inset 0 0 0 1px rgba(240, 200, 72, 0.08);
    }
  }
`;

const StyledPublication = styled.li`
  .publication-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 30px;
    border: 1px solid rgba(201, 162, 39, 0.12);
    border-radius: var(--border-radius);
    background: linear-gradient(180deg, rgba(34, 31, 58, 0.82), rgba(12, 10, 24, 0.9));
    transition: var(--transition);

    &.is-clickable {
      cursor: pointer;

      &:hover,
      &:focus-visible {
        border-color: var(--green);
        background: linear-gradient(180deg, rgba(43, 38, 63, 0.9), rgba(12, 10, 24, 0.96));
        transform: translateY(-4px);
        outline: none;
      }
    }
  }

  .publication-top {
    ${({ theme }) => theme.mixins.flexBetween};
    gap: 10px;
    margin-bottom: 20px;
  }

  .status {
    display: inline-flex;
    align-items: center;
    padding: 5px 10px;
    border: 1px solid var(--green);
    border-radius: 999px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &.status-published {
      background: var(--green);
      color: var(--dark-navy);
    }

    &.status-under-review {
      background: rgba(156, 207, 99, 0.08);
    }

    &.status-in-preparation {
      border-color: var(--dark-slate);
      color: var(--slate);
    }

    &.status-management-plan {
      border-color: var(--blue);
      color: var(--blue);
    }

    &.status-c2c-strategy {
      border-color: var(--pink);
      color: var(--pink);
    }
  }

  .category {
    display: inline-flex;
    align-items: center;
    margin-bottom: 20px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .year {
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .publication-title {
    margin: 0 0 10px;
    color: var(--white);
    font-size: var(--fz-xxl);
    line-height: 1.35;
  }

  .publication-meta {
    margin-bottom: 10px;
    color: var(--light-slate);
    font-size: var(--fz-sm);
    line-height: 1.7;
  }

  .publication-description {
    color: var(--light-slate);
    font-size: var(--fz-md);
    line-height: 1.7;
  }

  .publication-description a {
    ${({ theme }) => theme.mixins.inlineLink};
  }

  .publication-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0;
    margin: 20px 0 0;
    list-style: none;

    li {
      padding: 3px 9px;
      border: 1px solid rgba(201, 162, 39, 0.3);
      border-radius: 999px;
      color: var(--green);
      font-family: var(--font-mono);
      font-size: 10px;
      letter-spacing: 0.03em;
    }
  }

  .publication-link {
    ${({ theme }) => theme.mixins.flexCenter};
    margin-left: auto;
    color: var(--lightest-slate);
  }

  .publication-link svg {
    width: 20px;
    height: 20px;
  }

  .publication-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
  }

  .pdf-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 10px 20px;
    border: 1px solid var(--green);
    border-radius: var(--border-radius);
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-decoration: none;
    transition: var(--transition);

    &:hover,
    &:focus {
      background: rgba(201, 162, 39, 0.1);
      outline: none;
    }

    svg {
      width: 14px;
      height: 14px;
      flex-shrink: 0;
    }
  }
`;

const CATEGORY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'Research Publication', label: 'Research Publication' },
  { id: 'Management Plan', label: 'Management Plan' },
  { id: 'C2C Strategy', label: 'C2C Strategy' },
];

const getCategory = frontmatter =>
  frontmatter.category ||
  (frontmatter.status === 'Management Plan' || frontmatter.status === 'C2C Strategy'
    ? frontmatter.status
    : 'Research Publication');

const Publications = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const data = useStaticQuery(graphql`
    query {
      publications: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/publications/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              authors
              venue
              category
              status
              year
              external
              pdf
              linkLabel
              links {
                label
                url
              }
              tags
            }
            html
          }
        }
      }
    }
  `);

  const statusOrder = {
    Published: 0,
    'Under Review': 1,
    'In Preparation': 2,
    'Management Plan': 3,
    'C2C Strategy': 4,
  };
  const categoryOrder = {
    'Research Publication': 0,
    'Management Plan': 1,
    'C2C Strategy': 2,
  };
  const publications = data.publications.edges
    .filter(({ node }) => node)
    .sort((a, b) => {
      const ca = categoryOrder[getCategory(a.node.frontmatter)] ?? 9;
      const cb = categoryOrder[getCategory(b.node.frontmatter)] ?? 9;
      if (ca !== cb) {
        return ca - cb;
      }

      const sa = statusOrder[a.node.frontmatter.status] ?? 3;
      const sb = statusOrder[b.node.frontmatter.status] ?? 3;
      return sa - sb;
    });
  const filters = CATEGORY_FILTERS.filter(
    filter =>
      filter.id === 'all' ||
      publications.some(({ node }) => getCategory(node.frontmatter) === filter.id),
  );
  const filteredPublications =
    activeFilter === 'all'
      ? publications
      : publications.filter(({ node }) => getCategory(node.frontmatter) === activeFilter);
  const revealTitle = useRef(null);
  const revealCards = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealCards.current.forEach((ref, index) => sr.reveal(ref, srConfig(index * 80)));
  }, []);

  return (
    <StyledPublicationsSection id="publications">
      <h2 className="numbered-heading" ref={revealTitle}>
        Publications, Reports &amp; Strategies
      </h2>

      <div className="publication-filters" aria-label="Filter publications by category">
        {filters.map(filter => (
          <button
            className={`filter-button${activeFilter === filter.id ? ' active' : ''}`}
            key={filter.id}
            type="button"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              setActiveFilter(filter.id);
            }}
            aria-pressed={activeFilter === filter.id}>
            {filter.label}
          </button>
        ))}
      </div>

      <ul className="publications-grid">
        {filteredPublications.map(({ node }, index) => {
          const { frontmatter, html } = node;
          const {
            title,
            authors,
            venue,
            status,
            year,
            external,
            pdf,
            linkLabel,
            links,
            tags,
          } = frontmatter;
          const primaryLink = pdf ? withPrefix(pdf) : external;
          const categoryLabel = getCategory(frontmatter);
          const actionLinks =
            links && links.length > 0
              ? links
              : pdf
                ? [{ label: linkLabel || 'View Manuscript', url: pdf }]
                : [];
          const statusClass = `status-${status.toLowerCase().replace(/\s+/g, '-')}`;
          const openPrimaryLink = () => {
            if (primaryLink) {
              window.open(primaryLink, '_blank', 'noopener,noreferrer');
            }
          };
          const handleKeyDown = event => {
            if (primaryLink && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              openPrimaryLink();
            }
          };

          return (
            <StyledPublication key={title} ref={el => (revealCards.current[index] = el)}>
              <div
                className={`publication-inner${primaryLink ? ' is-clickable' : ''}`}
                role={primaryLink ? 'link' : undefined}
                tabIndex={primaryLink ? 0 : undefined}
                onClick={openPrimaryLink}
                onKeyDown={handleKeyDown}>
                <div className="publication-top">
                  <span className={`status ${statusClass}`}>{status}</span>
                  <span className="year">{year}</span>
                  {external ? (
                    <a
                      className="publication-link"
                      href={external}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${title}`}
                      onClick={event => event.stopPropagation()}>
                      <Icon name="External" />
                    </a>
                  ) : null}
                </div>

                <span className="category">{categoryLabel}</span>
                <h3 className="publication-title">{title}</h3>
                <div className="publication-meta">
                  <div>{authors}</div>
                  <div>{venue}</div>
                </div>
                <div
                  className="publication-description"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
                {tags && tags.length > 0 && (
                  <ul className="publication-tags">
                    {tags.map((tag, i) => (
                      <li key={i}>{tag}</li>
                    ))}
                  </ul>
                )}
                {actionLinks.length > 0 && (
                  <div className="publication-actions">
                    {actionLinks.map(({ label, url }) => (
                      <a
                        key={`${title}-${url}`}
                        className="pdf-link"
                        href={withPrefix(url)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${label}: ${title}`}
                        onClick={event => event.stopPropagation()}>
                        <Icon name="External" />
                        {label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </StyledPublication>
          );
        })}
      </ul>
    </StyledPublicationsSection>
  );
};

export default Publications;
